import Vue from 'vue';

import { Response } from '../../../core/types/http';

const FILES_API_URL = '/api/assets/v1/files';

export interface MoveObject {
  fileIds: string[];
  folderIds: string[];
}

export default {
  /**
   * Searches for files
   *
   * @param config - search config
   * @param config.top - number of elements per page
   * @param config.skip - number of elements to skip
   * @param config.filter - filters on properties of an file
   * @param config.attributes - comma delimited list of attribute fieldNames
   * @param beforeRequestCb - callback to execute before the request is sent
   * @returns the api response or error
   */
  async search(
    searchConfig: {
      top: number;
      skip: number;
      filter: string;
      attributes: string;
      sortBy: string;
    },
    beforeRequestCb: ((request: unknown) => void) | null = null
  ): Response<unknown> {
    return Vue.http.get(`${FILES_API_URL}/search`, {
      params: searchConfig,
      before(request: unknown) {
        if (beforeRequestCb) {
          beforeRequestCb(request);
        }
      },
    }) as Response<unknown>;
  },

  /**
   * Gets the form for a file
   *
   * @param filesIds - the IDs of each files with comma separator
   * @param beforeRequestCb - callback to execute before the request is sent
   * @returns the api response or error
   */
  async getFilesForm(
    filesIds: string,
    beforeRequestCb: ((request: unknown) => void) | null = null
  ): Response<unknown> {
    return Vue.http.get(`${FILES_API_URL}/form?fileIds=${filesIds}`, {
      before(request: unknown) {
        if (beforeRequestCb) {
          beforeRequestCb(request);
        }
      },
    }) as Response<unknown>;
  },

  /**
   * Gets all attributes for a file
   *
   * @param fileId - the ID of the file
   * @param beforeRequestCb - callback to execute before the request is sent
   * @returns the api response or error
   */
  async getFileAttributes(
    fileId: string,
    beforeRequestCb: ((request: unknown) => void) | null = null
  ): Response<unknown> {
    return Vue.http.get(`${FILES_API_URL}/${fileId}/attributes`, {
      before(request: unknown) {
        if (beforeRequestCb) {
          beforeRequestCb(request);
        }
      },
    }) as Response<unknown>;
  },

  /**
   * Creates a file
   *
   * @param file - the file to create
   * @param beforeRequestCb - callback to execute before the request is sent
   * @returns the api response or error
   */
  async createFile(
    file: object,
    beforeRequestCb: ((request: unknown) => void) | null = null
  ): Response<unknown> {
    return Vue.http.post(FILES_API_URL, file, {
      before(request: unknown) {
        if (beforeRequestCb) {
          beforeRequestCb(request);
        }
      },
    }) as Response<unknown>;
  },

  /**
   * Updates files in bulk mode
   * @param itemIds - List of files ids
   * @param attributes - List of attributes id and values
   * @param taskName - the task to execute the update
   * @param beforeRequestCb - callback to execute before the request is sent
   */
  async updateFiles(
    itemIds: string[],
    attributes: object[],
    taskName: string | null,
    beforeRequestCb: ((request: unknown) => void) | null = null
  ): Response<{ bulkActionId: string }> {
    return Vue.http.patch(
      `${FILES_API_URL}/bulk/values/${taskName ?? ''}`,
      [{ ItemIds: itemIds, attributes }],
      {
        before(request: unknown) {
          if (beforeRequestCb) {
            beforeRequestCb(request);
          }
        },
      }
    ) as Response<{ bulkActionId: string }>;
  },

  /**
   * Deletes a file
   *
   * @param fileId - the ID of the file
   * @param beforeRequestCb - callback to execute before the request is sent
   * @returns the api response or error
   */
  async deleteFile(
    fileId: string,
    beforeRequestCb: ((request: unknown) => void) | null = null
  ): Response<unknown> {
    return Vue.http.delete(`${FILES_API_URL}/${fileId}`, {
      before(request: unknown) {
        if (beforeRequestCb) {
          beforeRequestCb(request);
        }
      },
    }) as Response<unknown>;
  },

  /**
   * Returns the details of a file with the selected attributes
   *
   * @param fileId - the id of the file to get
   * @param attributes names of the attributes to select
   * @param beforeRequestCb - callback to execute before the request is sent
   * @returns the api response or error
   */
  async getFileById(
    fileId: string,
    attributes: string,
    beforeRequestCb: ((request: unknown) => void) | null = null
  ): Response<unknown> {
    return Vue.http.get(`${FILES_API_URL}/${fileId}?attributes=${attributes}`, {
      before(request: unknown) {
        if (beforeRequestCb) {
          beforeRequestCb(request);
        }
      },
    }) as Response<unknown>;
  },

  /**
   * Associates or dissociates files to folders
   *
   * @param associate - array of files/folders pairs to associate
   * @param dissociate - array of files/folders pairs to dissociate
   * @param beforeRequestCb - callback to execute before the request is sent
   * @param taskName - the task to execute the move
   * @returns the api response or error
   */
  async moveFiles(
    associate: MoveObject[],
    dissociate: MoveObject[],
    taskName: string | null,
    beforeRequestCb: ((request: unknown) => void) | null = null
  ): Response<{ bulkActionId: string }> {
    return Vue.http.post(
      `${FILES_API_URL}/bulk/move/${taskName ?? ''}`,
      { associate, dissociate },
      {
        before(request: unknown) {
          if (beforeRequestCb) {
            beforeRequestCb(request);
          }
        },
      }
    ) as Response<{ bulkActionId: string }>;
  },

  /**
   * Gets form global to all files
   *
   * @param beforeRequestCb - callback to execute before the request is sent
   * @returns the api response or error
   */
  async getGlobalForm(
    beforeRequestCb: ((request: unknown) => void) | null = null
  ): Response<unknown> {
    return Vue.http.get(`${FILES_API_URL}/form`, {
      before(request: unknown) {
        if (beforeRequestCb) {
          beforeRequestCb(request);
        }
      },
    }) as Response<unknown>;
  },

  /**
   * Triggers a bulk download of files
   *
   * @param fileIds - the ids of the files to download
   * @param beforeRequestCb - callback to execute before the request is sent
   * @param taskName - the task to execute the download
   * @returns the api response or error
   */
  async downloadFiles(
    fileIds: string[],
    taskName: string | null,
    beforeRequestCb: ((request: unknown) => void) | null = null
  ): Response<unknown> {
    return Vue.http.post(`${FILES_API_URL}/bulk/download/${taskName ?? ''}`, fileIds, {
      before(request: unknown) {
        if (beforeRequestCb) {
          beforeRequestCb(request);
        }
      },
    }) as Response<unknown>;
  },

  async getMediaUrlById(
    fileId: string,
    beforeRequestCb: ((request: unknown) => void) | null = null
  ): Response<unknown> {
    return Vue.http.get(`${FILES_API_URL}/${fileId}/media`, {
      before(request: unknown) {
        if (beforeRequestCb) {
          beforeRequestCb(request);
        }
      },
    }) as Response<unknown>;
  },
};
